import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@wehunt/core-web'
import { useTranslation } from 'react-i18next'
import '../map/map.css'
import { useApiFetch } from '../api/useApiFetch'
import { type Area, useAreas } from '../areas/useAreas'
import { useBaseMap } from '../basemap/useBaseMap'
import { HeatmapIndicator } from '../heatmap/HeatmapIndicator'
import {
  HeatmapContextPoller,
  type HeatmapContextState,
} from '../heatmap/HeatmapContextPoller'
import { useContexts } from '../heatmap/useContexts'
import { MapView, type HeatmapLayerEntry } from '../map/MapView'
import { NatlinkLogo } from '../map/NatlinkLogo'
import { buildSubAreaLabels, sortSubAreasForDisplay } from '../settings/subAreaLabels'
import { SettingsSheet } from '../settings/SettingsSheet'
import { useMapSettings } from '../settings/useMapSettings'
import { ErrorScreen } from './ErrorScreen'
import { SplashScreen } from './SplashScreen'

export function MapScreen() {
  const { t, i18n } = useTranslation()
  const { signOut } = useAuth()
  const call = useApiFetch()
  const { phase, spec, retry } = useBaseMap(call)
  // Borders are supplementary: useAreas's phase never gates RENDERING the way
  // useBaseMap's does — a failed /areas fetch must not stop the map from
  // drawing, it just leaves areas/lines at their empty defaults. It is read
  // only to tell the settings sheet whether a refresh is still running.
  const { areas, lines, phase: areasPhase, retry: retryAreas } = useAreas(call)

  const {
    showBorders,
    hiddenSubIds,
    heatmapOpacity,
    toggleShowBorders,
    toggleSub,
    setHeatmapOpacity,
  } = useMapSettings()

  const [sheetOpen, setSheetOpen] = useState(false)

  // Which surveillance areas this deployment shows, and what to call them.
  // Read from the API (ultimately configuration/asf-fin) rather than built in,
  // so adding an area needs no deploy of this app. A failed fetch leaves the
  // list empty: the base map still draws, with no heatmap chrome at all,
  // rather than polling a guessed context.
  const { contexts, retry: retryContexts } = useContexts(call)

  // One entry per area, keyed by context id. Each HeatmapContextPoller below
  // owns its own poll machine and reports here; this screen only collects
  // what they report, so no hook is called per list item (which the rules of
  // hooks forbid) and each area's clock stays independent.
  const [heatmapStates, setHeatmapStates] = useState<Record<string, HeatmapContextState>>({})

  // Stable, and compares before storing: a poller re-renders whenever this
  // state changes, so an unconditional store would loop.
  const handleHeatmapState = useCallback((next: HeatmapContextState) => {
    setHeatmapStates((prev) => {
      const current = prev[next.contextId]
      if (
        current &&
        current.phase === next.phase &&
        current.layer === next.layer &&
        current.coverage === next.coverage &&
        current.name === next.name &&
        current.retry === next.retry
      ) {
        return prev
      }
      return { ...prev, [next.contextId]: next }
    })
  }, [])

  // Ordered by the context list, not by whichever area's poll answered first,
  // so the chrome and the layer stack keep the order the operator chose in
  // exportGroups.
  const orderedStates = useMemo(
    () => contexts.map((context) => heatmapStates[context.id]).filter((state) => state != null),
    [contexts, heatmapStates],
  )

  const heatmapLayers = useMemo<HeatmapLayerEntry[]>(
    () =>
      orderedStates
        .filter((state) => state.layer !== null)
        .map((state) => ({ contextId: state.contextId, layer: state.layer! })),
    [orderedStates],
  )

  // Intl.NumberFormat does the ×100, decimal separator and percent spacing
  // per locale in one step — a Finnish reader needs "0,5 %" (comma, narrow
  // no-break space), not "0.5%" with an English decimal point.
  const coverageRows = useMemo(() => {
    const format = new Intl.NumberFormat(i18n.language, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })
    return orderedStates
      .filter((state) => state.coverage != null)
      .map((state) => ({
        contextId: state.contextId,
        name: state.name,
        value: format.format(state.coverage!),
      }))
  }, [orderedStates, i18n.language])

  // The same label the sheet lists and MapView draws on the map — computed
  // once here so the two can never disagree about what an unnamed area is
  // called.
  const unnamedLabel = t('unnamed_area')
  const subAreaLabels = useMemo(
    () => buildSubAreaLabels(areas, unnamedLabel),
    [areas, unnamedLabel],
  )
  const subLabel = useCallback(
    (area: Area) => subAreaLabels.get(area.id) ?? unnamedLabel,
    [subAreaLabels, unnamedLabel],
  )
  const subAreaOptions = useMemo(
    () =>
      sortSubAreasForDisplay(areas, subAreaLabels).map((area) => ({
        id: area.id,
        label: subAreaLabels.get(area.id) ?? unnamedLabel,
      })),
    [areas, subAreaLabels, unnamedLabel],
  )

  // Restarts both polls exactly as they run at page load — the heatmap's
  // fast/slow/give-up clock and the areas fetch both start over from zero,
  // the same as a fresh mount.
  const handleRefresh = useCallback(() => {
    // Every area's poll, plus the context list itself — a refresh after an
    // area was added should pick it up without a page reload.
    retryContexts()
    for (const state of orderedStates) state.retry()
    retryAreas()
  }, [retryContexts, orderedStates, retryAreas])

  // Both of the phases a refresh actually passes through count as pending, not
  // just the first request: after the initial response the heatmap usually
  // reports OUTDATED or PENDING and keeps polling in the background, and
  // pressing refresh again during that window only restarts a poll that is
  // already running. Disabling for the whole window also matches what the map
  // shows — the "updating" pill is visible for exactly the same period.
  // 'stalled' and 'error' deliberately re-enable it: at that point retrying is
  // the one thing that might help.
  // Any area still working counts: the refresh control restarts all of them
  // at once, so it stays disabled until none of them is mid-flight.
  const refreshPending =
    orderedStates.some((state) => state.phase === 'loading' || state.phase === 'updating') ||
    areasPhase === 'loading'

  if (phase === 'loading') return <SplashScreen />
  if (phase === 'error' || !spec) return <ErrorScreen onRetry={retry} />

  return (
    <div className="asf-map-root" data-testid="asf-map-screen">
      {/* One per area, rendering nothing: see HeatmapContextPoller for why a
          component is what instantiates a per-area poll machine. */}
      {contexts.map((context) => (
        <HeatmapContextPoller
          key={context.id}
          context={context}
          call={call}
          onState={handleHeatmapState}
        />
      ))}
      <MapView
        basemap={spec}
        heatmaps={heatmapLayers}
        areas={areas}
        lines={lines}
        showBorders={showBorders}
        hiddenSubIds={hiddenSubIds}
        heatmapOpacity={heatmapOpacity}
        subLabel={subLabel}
      />
      <div className="asf-chrome asf-chrome-start">
        <NatlinkLogo />
      </div>
      {coverageRows.length > 0 && (
        <div className="asf-chrome asf-chrome-end">
          {/* One row per area, each naming its own. The label is VISIBLE
              rather than an aria-label: a bare percentage floating over map
              detail reads as an unexplained number, and it was getting lost
              against the imagery. Showing the name also means the element's
              accessible name comes from its own text, so no separate
              aria-label is needed (and cannot drift from it) — and with two
              areas on screen, an unlabelled figure would not even say which
              area it measures. */}
          {coverageRows.map((row) => (
            <span key={row.contextId} className="asf-coverage">
              {t('area_coverage_label', { name: row.name, value: row.value })}
            </span>
          ))}
        </div>
      )}
      <div className="asf-chrome asf-chrome-bottom-end">
        {/* Always OPENS rather than toggling: the sheet's own outside-click
            listener treats this button as "outside" while the sheet is
            already open, so a toggle here would race that listener — whoever
            's setSheetOpen call landed last would decide whether the sheet
            ends up open or closed. Unconditional `true` sidesteps the race
            instead of depending on event-batching order; closing is what the
            sheet's own close button / Escape / outside click are for. */}
        <button
          type="button"
          className="asf-map-control"
          onClick={() => setSheetOpen(true)}
        >
          {t('settings')}
        </button>
      </div>
      <SettingsSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onRefresh={handleRefresh}
        refreshPending={refreshPending}
        showBorders={showBorders}
        onToggleShowBorders={toggleShowBorders}
        subAreas={subAreaOptions}
        hiddenSubIds={hiddenSubIds}
        onToggleSub={toggleSub}
        heatmapOpacity={heatmapOpacity}
        onHeatmapOpacityChange={setHeatmapOpacity}
        onSignOut={() => void signOut()}
      />
      <HeatmapIndicator areas={orderedStates} />
    </div>
  )
}
